<template>
  <v-dialog
    :model-value="modelValue"
    max-width="700"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="cookie-config-modal">
      <!-- Header -->
      <v-card-title class="config-header">
        <div class="d-flex align-items-center justify-space-between w-100">
          <div class="d-flex align-items-center">
            <v-icon
              class="mr-2"
              color="primary"
            >
              mdi-cog
            </v-icon>
            <span>{{ t("cookies.config.titulo") }}</span>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            :aria-label="t('general.cerrar')"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <!-- Contenido -->
      <v-card-text class="config-content">
        <p class="config-intro">
          {{ t("cookies.config.intro") }}
        </p>

        <!-- Categoría: Análisis -->
        <div class="cookie-category">
          <div class="category-header">
            <div class="category-info">
              <h3 class="category-title">
                {{ t("cookies.config.analisis.titulo") }}
              </h3>
              <p class="category-description">
                {{ t("cookies.config.analisis.descripcion") }}
              </p>
            </div>
            <v-switch
              v-model="localPreferences.analytics"
              color="primary"
              hide-details
              :aria-label="t('cookies.config.analisis.toggle')"
            />
          </div>

          <!-- Detalles de Google Analytics -->
          <div
            v-if="localPreferences.analytics"
            class="category-details"
          >
            <div class="detail-item">
              <span class="detail-label">
                <v-icon
                  size="small"
                  class="mr-1"
                >mdi-domain</v-icon>
                {{ t("cookies.config.analisis.proveedor") }}:
              </span>
              <span class="detail-value">Google Analytics (GA4)</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">
                <v-icon
                  size="small"
                  class="mr-1"
                >mdi-clock-outline</v-icon>
                {{ t("cookies.config.analisis.duracion") }}:
              </span>
              <span class="detail-value">2 años</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">
                <v-icon
                  size="small"
                  class="mr-1"
                >mdi-cookie</v-icon>
                {{ t("cookies.config.analisis.cookies") }}:
              </span>
              <span class="detail-value">_ga, _ga_*</span>
            </div>
          </div>

          <!-- Enlace a política -->
          <div class="policy-link">
            <v-icon
              size="small"
              class="mr-1"
            >
              mdi-information-outline
            </v-icon>
            <a
              href="#"
              class="link-primary"
              @click.prevent="goToPoliticaCookies"
            >
              {{ t("cookies.config.verPolitica") }}
            </a>
          </div>
        </div>

        <!-- Aviso legal -->
        <v-alert
          type="info"
          variant="tonal"
          class="mt-4"
          density="compact"
        >
          <template #prepend>
            <v-icon size="small">
              mdi-information
            </v-icon>
          </template>
          {{ t("cookies.config.avisoLegal") }}
        </v-alert>
      </v-card-text>

      <v-divider />

      <!-- Acciones -->
      <v-card-actions class="config-actions pa-4">
        <v-btn
          variant="text"
          class="flex-grow-1"
          @click="emitRejectAll"
        >
          {{ t("cookies.config.rechazarTodo") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          class="flex-grow-1"
          @click="emitSave"
        >
          {{ t("cookies.config.guardar") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="flex-grow-1"
          @click="emitAcceptAll"
        >
          {{ t("cookies.config.aceptarTodo") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { getLocaleFromRoute } from "@/router";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// Obtener locale actual
const currentLocale = computed(() => getLocaleFromRoute(route));

// Helper para navegar manteniendo el idioma
const navigateTo = (baseName, options = {}) => {
  const routeName = currentLocale.value === "en" ? `${baseName}-en` : baseName;
  router.push({ name: routeName, ...options });
};

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  preferences: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  "update:modelValue",
  "save",
  "accept-all",
  "reject-all",
]);

const localPreferences = ref({ ...props.preferences });

// Sincronizar cuando cambien las props
watch(
  () => props.preferences,
  (newVal) => {
    localPreferences.value = { ...newVal };
  },
  { deep: true }
);

const close = () => {
  emit("update:modelValue", false);
};

const goToPoliticaCookies = () => {
  close();
  navigateTo("politicaCookies");
};

const emitSave = () => {
  emit("save", { ...localPreferences.value });
  close();
};

const emitAcceptAll = () => {
  localPreferences.value.analytics = true;
  emit("accept-all", { ...localPreferences.value });
  close();
};

const emitRejectAll = () => {
  localPreferences.value.analytics = false;
  emit("reject-all", { ...localPreferences.value });
  close();
};
</script>

<style scoped>
.cookie-config-modal {
  border-radius: 16px !important;
}

.config-header {
  padding: 20px 24px;
  background-color: rgba(var(--v-theme-primary), 0.03);
  font-size: 1.25rem;
  font-weight: 600;
}

.config-content {
  padding: 24px !important;
}

.config-intro {
  font-size: 0.9375rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 24px;
}

.cookie-category {
  background-color: rgba(var(--v-theme-primary), 0.03);
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid rgb(var(--v-theme-primary));
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.category-info {
  flex: 1;
}

.category-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 8px;
}

.category-description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 0;
}

.category-details {
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.875rem;
}

.detail-item:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.detail-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
}

.detail-value {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
}

.policy-link {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
}

.link-primary {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.link-primary:hover {
  opacity: 0.7;
  text-decoration: underline;
}

.config-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Responsive */
@media (max-width: 600px) {
  .config-header {
    padding: 16px 20px;
    font-size: 1.125rem;
  }

  .config-content {
    padding: 20px !important;
  }

  .cookie-category {
    padding: 16px;
  }

  .category-header {
    flex-direction: column;
  }

  .config-actions {
    flex-direction: column;
  }

  .config-actions .v-btn {
    width: 100% !important;
  }
}
</style>
